import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  createColumnMapping,
  ExcelProcessorConfig,
  formatValidationErrors,
  HeaderConfig,
  parseBoolean,
  ParsedExcelData,
  parseExcelData,
  readExcelFile,
  validateRequiredFields,
  ValidationError
} from '@/lib/excel';
import { calculateTotals } from '../hooks/useCalc';
import { roundNumber } from '@/lib/formatUtils';
import { toast } from '@/hooks/use-toast';
import { MA_CHUNG_TU } from '@/constants';

// Re-export types from @/lib/excel for convenience
export type { ValidationError, ColumnMapping } from '@/lib/excel';

const HEADERS = {
  // Main fields
  MA_DON_VI: 'Mã đơn vị',
  SO_CT: 'Số c/từ*',
  NGAY_CT: 'Ngày chứng từ*',
  MA_NT: 'Ngoại tệ*',
  TY_GIA: 'Tỷ giá',
  LOAI_XUAT: 'Loại xuất*',
  MA_GD: 'Giao dịch*',
  MA_KH: 'Mã đối tượng*',
  TEN_KH: 'Tên đối tượng',
  DIEN_GIAI: 'Diễn giải',
  // Detail fields
  MA_VT: 'Mã sản phẩm*',
  TEN_VT: 'Tên sản phẩm',
  DVT: 'Đvt*',
  MA_KHO: 'Mã kho*',
  MA_LO: 'Mã lô',
  MA_VI_TRI: 'Mã vị trí',
  SO_LUONG: 'Số lượng*',
  PX_DD: 'Đích danh',
  GIA: 'Giá',
  TIEN: 'Tiền',
  TK_CO: 'Tk có',
  LY_DO_XUAT: 'Lý do xuất',
  TK_NO: 'Tk nợ',
  MA_BP: 'Bộ phận',
  MA_PHI: 'Phí',
  MA_SP: 'Sản phẩm',
  MA_LSX: 'Lệnh sản xuất'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  // Main fields
  { header: HEADERS.SO_CT, key: 'so_ct', type: 'main' },
  { header: HEADERS.NGAY_CT, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.MA_NT, key: 'ma_nt', type: 'main' },
  { header: HEADERS.TY_GIA, key: 'ty_gia', type: 'main' },
  { header: HEADERS.LOAI_XUAT, key: 'status', type: 'main' },
  { header: HEADERS.MA_GD, key: 'ma_gd', type: 'main' },
  { header: HEADERS.MA_KH, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KH, key: 'ten_kh', type: 'main' },
  { header: HEADERS.DIEN_GIAI, key: 'dien_giai', type: 'main' },
  // Detail fields
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'chi_tiet' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'chi_tiet' },
  { header: HEADERS.DVT, key: 'dvt', type: 'chi_tiet' },
  { header: HEADERS.MA_KHO, key: 'ma_kho', type: 'chi_tiet' },
  { header: HEADERS.MA_LO, key: 'ma_lo', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRI, key: 'ma_vi_tri', type: 'chi_tiet' },
  { header: HEADERS.SO_LUONG, key: 'so_luong', type: 'chi_tiet' },
  { header: HEADERS.PX_DD, key: 'px_dd', type: 'chi_tiet' },
  { header: HEADERS.GIA, key: 'gia_nt', type: 'chi_tiet' },
  { header: HEADERS.TIEN, key: 'tien_nt', type: 'chi_tiet' },
  { header: HEADERS.TK_CO, key: 'tk_vt', type: 'chi_tiet' },
  { header: HEADERS.LY_DO_XUAT, key: 'ma_nx', type: 'chi_tiet' },
  { header: HEADERS.TK_NO, key: 'tk_du', type: 'chi_tiet' },
  { header: HEADERS.MA_BP, key: 'ma_bp', type: 'chi_tiet' },
  { header: HEADERS.MA_PHI, key: 'ma_phi', type: 'chi_tiet' },
  { header: HEADERS.MA_SP, key: 'ma_sp', type: 'chi_tiet' },
  { header: HEADERS.MA_LSX, key: 'ma_lsx', type: 'chi_tiet' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'ma_kh', 'ma_gd'],
  chi_tiet: [
    'ma_vt',
    'dvt',
    'ma_kho',
    'ma_lo',
    'ma_vi_tri',
    'tk_vt',
    'tk_du',
    'ma_nx',
    'ma_bp',
    'ma_phi',
    'ma_sp',
    'ma_lsx'
  ]
};

const DEFAULT_MA_NX_CODE = 'NB';

type ExcelCellValue = string | number | boolean | Date | null | undefined;
type ExcelSection = Record<string, ExcelCellValue[]>;
type UUIDLookupSection = Record<string, Array<string | null>>;
type UUIDLookupResponse = {
  main?: UUIDLookupSection;
  chi_tiet?: UUIDLookupSection;
};
type UUIDLookupPayload = ReturnType<typeof UUIDPayload>;

const toText = (value: ExcelCellValue): string => (value === null || value === undefined ? '' : String(value));
const toRoundable = (value: ExcelCellValue): string | number | null | undefined =>
  typeof value === 'string' || typeof value === 'number' || value === null || value === undefined
    ? value
    : String(value);
const toDateCell = (value: ExcelCellValue): string | number | Date | undefined =>
  typeof value === 'string' || typeof value === 'number' || value instanceof Date ? value : undefined;

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const getFieldValues = <T>(...candidates: Array<T[] | undefined>): T[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      const hasMeaningfulValue = candidate.some(
        value => value !== undefined && value !== null && String(value).trim() !== ''
      );
      if (hasMeaningfulValue) {
        return candidate;
      }
    }
  }
  return [];
};

const getFieldValuesWithFallback = <T>(candidates: T[] | undefined, fallback: T[]): T[] => {
  const values = getFieldValues(candidates);
  return values.length > 0 ? values : fallback;
};

const hasData = (item: Record<string, unknown>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: ExcelSection, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section?.[f] || []]));

  const main = data.main || {};
  const chi_tiet = data.chi_tiet || {};

  const seenDates = new Set<string>();
  const chungTuArr: Array<{ ma_ct: string; ngay_hl: string }> = [];

  (main.so_ct || []).forEach((_, index: number) => {
    const ngay = convertExcelDate(toDateCell(main.ngay_ct?.[index])) || format(new Date(), 'yyyy-MM-dd');
    const ngay_hl = ngay || format(new Date(), 'yyyy-MM-dd');
    if (seenDates.has(ngay_hl)) return;
    seenDates.add(ngay_hl);

    chungTuArr.push({
      ma_ct: MA_CHUNG_TU.TON_KHO.PHIEU_XUAT_KHO,
      ngay_hl
    });
  });

  return {
    main: {
      ...build(main, UUID_FIELDS.main),
      ma_gd: (main.ma_gd || []).map(val => (val ? `${val}` : val))
    },
    chi_tiet: {
      ...build(chi_tiet, UUID_FIELDS.chi_tiet),
      tk_du: getFieldValuesWithFallback(chi_tiet.tk_du, ['1561']),
      ma_nx: getFieldValuesWithFallback(chi_tiet.ma_nx, [DEFAULT_MA_NX_CODE])
    },
    chung_tu: chungTuArr
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const processChiTietRows = (
  groupedRecords: Map<string, ImportBody>,
  main: ExcelSection,
  chi_tiet: ExcelSection,
  UUIDResponse: UUIDLookupResponse,
  rowCount: number
): Map<string, ImportBody['chi_tiet']> => {
  const chiTietMap = new Map<string, ImportBody['chi_tiet']>();
  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'chi_tiet').map(c => c.key);

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = toText(main.so_ct?.[i]).trim();
    const recordKey = `${documentNumber}`;

    const record = groupedRecords.get(recordKey);
    if (!record) continue;

    const description = toText(main.dien_giai?.[i]).trim();
    if ((!record.dien_giai || record.dien_giai.trim() === '') && description) {
      record.dien_giai = description;
    }

    const quantity = roundNumber(toRoundable(chi_tiet.so_luong?.[i]), 6);
    const price = roundNumber(toRoundable(chi_tiet.gia_nt?.[i]), 6);
    const amount = roundNumber(toRoundable(chi_tiet.tien_nt?.[i]), 6) || roundNumber(quantity * price, 6);
    const directDelivery = parseBoolean(toText(chi_tiet.px_dd?.[i]), false);

    const detailsForKey = chiTietMap.get(recordKey) || [];

    const detailItem = {
      line: detailsForKey.length + 1,
      ma_vt: UUIDResponse.chi_tiet?.ma_vt?.[i] || null,
      dvt: UUIDResponse.chi_tiet?.dvt?.[i] || toText(chi_tiet.dvt?.[i]) || null,
      ma_kho: UUIDResponse.chi_tiet?.ma_kho?.[i] || null,
      ma_lo: toText(chi_tiet.ma_lo?.[i]) || undefined,
      ma_vi_tri: UUIDResponse.chi_tiet?.ma_vi_tri?.[i] || toText(chi_tiet.ma_vi_tri?.[i]) || undefined,
      so_luong: quantity,
      px_dd: directDelivery ? 1 : 0,
      gia_nt: price,
      tien_nt: amount,
      gia: price,
      tien: amount,
      tk_vt: UUIDResponse.chi_tiet?.tk_vt?.[i] || null,
      tk_du: UUIDResponse.chi_tiet?.tk_du?.[i] || null,
      ma_nx: UUIDResponse.chi_tiet?.ma_nx?.[i] || null,
      ma_bp: UUIDResponse.chi_tiet?.ma_bp?.[i] || null,
      ma_phi: UUIDResponse.chi_tiet?.ma_phi?.[i] || null,
      ma_sp: UUIDResponse.chi_tiet?.ma_sp?.[i] || null,
      ma_lsx: UUIDResponse.chi_tiet?.ma_lsx?.[i] || '',
      line_yc: 0
    };

    if (hasData(detailItem, detailKeys)) {
      detailsForKey.push(detailItem);
      chiTietMap.set(recordKey, detailsForKey);
    }
  }

  return chiTietMap;
};

const processMainRows = (
  main: ExcelSection,
  UUIDResponse: UUIDLookupResponse,
  entityUnitUUID: string,
  rowCount: number,
  defaultMaGD: string,
  chiTietMap?: Map<string, ImportBody['chi_tiet']>
): Map<string, ImportBody> => {
  const groupedRecords = new Map<string, ImportBody>();

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = toText(main.so_ct?.[i]).trim();
    const recordKey = `${documentNumber}`;

    if (!groupedRecords.has(recordKey)) {
      const documentDate = convertExcelDate(toDateCell(main.ngay_ct?.[i])) || format(new Date(), 'yyyy-MM-dd');
      const transactionDate = documentDate;

      const detail = chiTietMap?.get(recordKey) || [];
      const { t_tien_nt, t_tien, t_so_luong } = calculateTotals(detail);

      const record: ImportBody = {
        so_ct: documentNumber || undefined,
        ngay_ct: documentDate,
        ngay_lct: transactionDate,
        ma_nt: UUIDResponse.main?.ma_nt?.[i] || toText(main.ma_nt?.[i]) || 'VND',
        ty_gia: toText(main.ty_gia?.[i]) || '1',
        ma_nx: UUIDResponse.main?.ma_nx?.[i] || toText(main.ma_nx?.[i]) || '',
        ma_gd: UUIDResponse.main?.ma_gd?.[i] || toText(main.ma_gd?.[i]) || defaultMaGD,
        ma_kh: UUIDResponse.main?.ma_kh?.[i] || '',
        ten_kh: toText(main.ten_kh?.[i]) || undefined,
        dien_giai: toText(main.dien_giai?.[i]).trim(),
        unit_id: entityUnitUUID,
        ma_ngv: '1',
        status: toText(main.status?.[i]) || '5',
        i_so_ct: 0,
        chi_tiet: detail,
        t_so_luong: roundNumber(t_so_luong, 6),
        t_tien: roundNumber(t_tien, 6),
        t_tien_nt: roundNumber(t_tien_nt, 6)
      };

      groupedRecords.set(recordKey, record);
    }
  }

  return groupedRecords;
};

const TransformToBodies = (
  dataRows: ExcelCellValue[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDLookupResponse,
  context: { entityUnitUUID: string; defaultMaGD?: string }
): ImportBody[] => {
  const main = parsedData.main || {};
  const chi_tiet = parsedData.chi_tiet || {};
  const rowCount = dataRows.length;
  const defaultMaGD = context.defaultMaGD || 'NB';

  const tempGroupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount, defaultMaGD);
  const chiTietMap = processChiTietRows(tempGroupedRecords, main, chi_tiet, uuidResponse, rowCount);
  const groupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount, defaultMaGD, chiTietMap);

  const records = Array.from(groupedRecords.values());
  records.forEach(record => {
    const key = `${record.so_ct || ''}`.trim();
    record.chi_tiet = chiTietMap.get(key) || [];
  });

  return records;
};

export interface ImportBody {
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  ma_nt?: string;
  ty_gia?: string;
  ma_nx?: string;
  ma_gd?: string;
  ma_kh: string;
  ten_kh?: string;
  dien_giai?: string;

  unit_id: string;
  ma_ngv: string;
  status?: string;
  i_so_ct?: number;
  chung_tu?: string;
  ma_nk?: string;
  t_tien?: number;
  t_tien_nt?: number;
  t_so_luong?: number;
  chi_tiet: Array<{
    line: number;
    ma_vt: string | null;
    dvt: string | null;
    ma_kho: string | null;
    ma_lo?: string;
    ma_vi_tri?: string;
    so_luong: number;
    px_dd?: number;
    gia_nt?: number;
    tien_nt?: number;
    gia?: number;
    tien?: number;
    tk_vt?: string | null;
    tk_du?: string | null;
    ma_nx?: string | null;
    ma_bp?: string | null;
    ma_phi?: string | null;
    ma_sp?: string | null;
    ma_lsx?: string;
    line_yc?: number;
  }>;
}

export interface ImportErrorData {
  errors: ValidationError[];
  columnMapping: Record<string, ColumnMapping>;
  errorFileUrl?: string;
  errorFileName?: string;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};

export const handleImportExcel = async (
  getUUIDByCode: (payload: UUIDLookupPayload) => Promise<UUIDLookupResponse>,
  onSubmit: (bodies: ImportBody[]) => Promise<void>,
  entityUnitUUID: string,
  onError?: (errorData: ImportErrorData) => void
): Promise<void> => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.xlsx,.xls';

  input.onchange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
      const { headers, dataRows } = await readExcelFile(file);

      if (dataRows.length === 0) {
        toast({
          description: 'File Excel không có dữ liệu',
          variant: 'destructive'
        });
        return;
      }

      const columnMapping = createColumnMapping(headers, ExcelConfig.HeaderConfig);
      const parsedData = parseExcelData(columnMapping, dataRows);

      const emitErrors = (validationErrors: ValidationError[]) => {
        if (onError) {
          onError({ errors: validationErrors, columnMapping });
        } else {
          const errorMessage = formatValidationErrors(validationErrors);
          toast({
            description: errorMessage,
            variant: 'destructive'
          });
        }
      };

      const aggregatedErrors: ValidationError[] = [];
      const requiredFieldErrors = validateRequiredFields(columnMapping, dataRows);

      if (requiredFieldErrors.length > 0) {
        aggregatedErrors.push(...requiredFieldErrors);
      }

      const uuidPayload = ExcelConfig.UUIDPayload?.(parsedData);
      const UUIDResponse = uuidPayload ? await getUUIDByCode(uuidPayload) : null;
      if (!UUIDResponse) {
        toast({
          description: 'Không nhận được UUID response',
          variant: 'destructive'
        });
        return;
      }

      if (ExcelConfig.ValidateUUID) {
        const validationErrors = ExcelConfig.ValidateUUID(UUIDResponse, columnMapping);
        if (validationErrors.length > 0) {
          aggregatedErrors.push(...validationErrors);
        }
      }

      if (aggregatedErrors.length > 0) {
        emitErrors(aggregatedErrors);
        return;
      }

      const bodies = ExcelConfig.Transform(dataRows, parsedData, UUIDResponse, { entityUnitUUID });
      await onSubmit(bodies);
    } catch (error) {
      console.error('Lỗi khi đọc file Excel:', error);
      toast({
        description: error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập dữ liệu Excel',
        variant: 'destructive'
      });
    }
  };

  input.click();
};
